import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';

const statusStyle = {
  Good:              { background: '#ECFDF5', color: '#065F46' },
  'Needs Attention': { background: '#FEF3C7', color: '#92400E' },
  Critical:          { background: '#FDECEA', color: '#B91C1C' },
};

const emptyForm = {
  name: '', domain: '', industry: '', contact: '', email: '',
};

function formatDate(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function Clients() {
  const navigate = useNavigate();
  const { session } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!session?.user?.id) return;
    let cancelled = false;
    async function fetchClients() {
      setLoading(true);
      setFetchError('');
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .eq('user_id', session.user.id)
        .order('created_at', { ascending: false });
      if (cancelled) return;
      if (error) {
        console.error('Supabase fetch error:', error);
        setFetchError('Failed to load clients. Please refresh.');
      } else {
        setClients(data || []);
      }
      setLoading(false);
    }
    fetchClients();
    return () => { cancelled = true; };
  }, [session]);

  async function fetchClients() {
    setLoading(true);
    setFetchError('');
    const { data, error } = await supabase
      .from('clients')
      .select('*')
      .eq('user_id', session.user.id)
      .order('created_at', { ascending: false });
    if (error) {
      console.error('Supabase fetch error:', error);
      setFetchError('Failed to load clients. Please refresh.');
    } else {
      setClients(data || []);
    }
    setLoading(false);
  }

  const filtered = clients.filter(c =>
    (c.business_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (c.domain || '').toLowerCase().includes(search.toLowerCase()) ||
    (c.industry || '').toLowerCase().includes(search.toLowerCase())
  );

  function handleFormChange(e) {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
    setFormError('');
  }

  async function handleAddClient(e) {
    e.preventDefault();
    if (!form.name || !form.domain) {
      setFormError('Business name and domain are required.');
      return;
    }
    setSubmitting(true);
    const { error } = await supabase.from('clients').insert({
      user_id:       session.user.id,
      business_name: form.name,
      domain:        form.domain,
      industry:      form.industry || null,
      contact_name:  form.contact || null,
      contact_email: form.email   || null,
      status:        'Good',
    });
    setSubmitting(false);
    if (error) {
      console.error('Supabase insert error:', error);
      setFormError(error.message || 'Failed to add client. Please try again.');
      return;
    }
    setForm(emptyForm);
    setModalOpen(false);
    fetchClients();
  }

  function handleClose() {
    setModalOpen(false);
    setForm(emptyForm);
    setFormError('');
  }

  return (
    <div>

      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        marginBottom: '2rem',
      }}>
        <div>
          <h1 style={{
            fontFamily: 'var(--font-heading)',
            fontSize: '2.2rem',
            fontWeight: 600,
            color: 'var(--charcoal)',
            lineHeight: 1.15,
            marginBottom: '0.25rem',
          }}>
            Clients
          </h1>
          <p style={{
            fontFamily: 'var(--font-body)',
            fontSize: '0.875rem',
            color: 'var(--charcoal-muted)',
          }}>
            {clients.length} client{clients.length !== 1 ? 's' : ''} · Manage accounts and run reports
          </p>
        </div>
        <button
          onClick={() => setModalOpen(true)}
          style={{
            background: 'var(--blush)',
            color: 'var(--charcoal)',
            fontFamily: 'var(--font-body)',
            fontWeight: 500,
            fontSize: '0.875rem',
            letterSpacing: '0.03em',
            padding: '0.65rem 1.3rem',
            borderRadius: 'var(--radius-sm)',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            flexShrink: 0,
            marginTop: '0.25rem',
            transition: 'background 0.2s ease',
          }}
          onMouseEnter={e => e.currentTarget.style.background = 'var(--blush-dark)'}
          onMouseLeave={e => e.currentTarget.style.background = 'var(--blush)'}
        >
          <span style={{ fontSize: '1rem' }}>＋</span> Add New Client
        </button>
      </div>

      {/* Search */}
      <div style={{ marginBottom: '1.75rem', maxWidth: '380px' }}>
        <input
          type="text"
          placeholder="Search clients by name, domain, or industry…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{
            fontFamily: 'var(--font-body)',
            fontSize: '0.875rem',
            color: 'var(--charcoal)',
            background: 'var(--white)',
            border: '1.5px solid var(--border)',
            borderRadius: 'var(--radius-sm)',
            padding: '0.6rem 0.9rem',
            width: '100%',
            outline: 'none',
          }}
          onFocus={e => e.target.style.borderColor = 'var(--blush-dark)'}
          onBlur={e => e.target.style.borderColor = 'var(--border)'}
        />
      </div>

      {/* States */}
      {fetchError && (
        <div style={{
          fontFamily: 'var(--font-body)',
          fontSize: '0.85rem',
          color: '#B91C1C',
          background: '#FDECEA',
          border: '1px solid #FCA5A5',
          borderRadius: 'var(--radius-sm)',
          padding: '0.75rem 1rem',
          marginBottom: '1.5rem',
        }}>
          {fetchError}
        </div>
      )}

      {loading ? (
        <div style={{
          textAlign: 'center',
          padding: '4rem 2rem',
          fontFamily: 'var(--font-heading)',
          fontSize: '1.1rem',
          color: 'var(--blush)',
        }}>
          Loading clients…
        </div>
      ) : clients.length === 0 && !search ? (
        <div style={{
          textAlign: 'center',
          padding: '4rem 2rem',
          background: 'var(--white)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-md)',
          boxShadow: 'var(--shadow-sm)',
        }}>
          <div style={{
            fontFamily: 'var(--font-heading)',
            fontSize: '1.4rem',
            fontWeight: 600,
            color: 'var(--charcoal)',
            marginBottom: '0.5rem',
          }}>
            No clients yet
          </div>
          <p style={{
            fontFamily: 'var(--font-body)',
            fontSize: '0.875rem',
            color: 'var(--charcoal-muted)',
            marginBottom: '1.5rem',
          }}>
            Add your first client to get started.
          </p>
          <button
            onClick={() => setModalOpen(true)}
            style={{
              background: 'var(--blush)',
              color: 'var(--charcoal)',
              fontFamily: 'var(--font-body)',
              fontWeight: 500,
              fontSize: '0.875rem',
              padding: '0.65rem 1.3rem',
              borderRadius: 'var(--radius-sm)',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            ＋ Add New Client
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '4rem 2rem',
          color: 'var(--charcoal-muted)',
          fontFamily: 'var(--font-body)',
          fontSize: '0.9rem',
        }}>
          No clients match your search.
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(310px, 1fr))',
          gap: '1.25rem',
        }}>
          {filtered.map(client => (
            <div key={client.id} style={{
              background: 'var(--white)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-md)',
              boxShadow: 'var(--shadow-sm)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              transition: 'box-shadow 0.2s ease',
            }}
              onMouseEnter={e => e.currentTarget.style.boxShadow = 'var(--shadow-md)'}
              onMouseLeave={e => e.currentTarget.style.boxShadow = 'var(--shadow-sm)'}
            >
              {/* Card top accent */}
              <div style={{
                height: '3px',
                background: client.status === 'Critical'
                  ? '#EF4444'
                  : client.status === 'Needs Attention'
                  ? 'var(--gold)'
                  : 'var(--blush)',
              }} />

              <div style={{ padding: '1.25rem 1.4rem', flex: 1 }}>
                {/* Name + status */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '0.35rem',
                }}>
                  <h3 style={{
                    fontFamily: 'var(--font-heading)',
                    fontSize: '1.15rem',
                    fontWeight: 600,
                    color: 'var(--charcoal)',
                    lineHeight: 1.2,
                    flex: 1,
                    marginRight: '0.75rem',
                  }}>
                    {client.business_name}
                  </h3>
                  <span style={{
                    ...statusStyle[client.status],
                    display: 'inline-flex',
                    alignItems: 'center',
                    fontFamily: 'var(--font-body)',
                    fontSize: '0.7rem',
                    fontWeight: 500,
                    letterSpacing: '0.04em',
                    padding: '0.2rem 0.65rem',
                    borderRadius: '999px',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}>
                    {client.status}
                  </span>
                </div>

                {/* Domain */}
                <p style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.8rem',
                  color: 'var(--blush-dark)',
                  marginBottom: '1rem',
                }}>
                  {client.domain}
                </p>

                {/* Meta rows */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1.1rem' }}>
                  <MetaRow label="Industry"    value={client.industry || '—'} />
                  <MetaRow label="Added"       value={formatDate(client.created_at)} />
                  <MetaRow label="Contact"     value={client.contact_name || '—'} />
                </div>
              </div>

              {/* Card footer buttons */}
              <div style={{
                display: 'flex',
                gap: '0.6rem',
                padding: '0.9rem 1.4rem',
                borderTop: '1px solid var(--border)',
                background: 'var(--off-white)',
              }}>
                <button
                  onClick={() => navigate('/reports')}
                  style={{
                    flex: 1,
                    fontFamily: 'var(--font-body)',
                    fontSize: '0.8rem',
                    fontWeight: 500,
                    color: 'var(--charcoal)',
                    background: 'var(--white)',
                    border: '1.5px solid var(--border)',
                    borderRadius: 'var(--radius-sm)',
                    padding: '0.5rem 0',
                    cursor: 'pointer',
                    transition: 'border-color 0.15s, background 0.15s',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.borderColor = 'var(--blush-dark)';
                    e.currentTarget.style.background = 'var(--blush-light)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.borderColor = 'var(--border)';
                    e.currentTarget.style.background = 'var(--white)';
                  }}
                >
                  View Report
                </button>
                <button
                  onClick={() => navigate('/run')}
                  style={{
                    flex: 1,
                    fontFamily: 'var(--font-body)',
                    fontSize: '0.8rem',
                    fontWeight: 500,
                    color: 'var(--white)',
                    background: 'var(--charcoal)',
                    border: '1.5px solid var(--charcoal)',
                    borderRadius: 'var(--radius-sm)',
                    padding: '0.5rem 0',
                    cursor: 'pointer',
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--charcoal-light)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'var(--charcoal)'}
                >
                  Run Report
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal overlay */}
      {modalOpen && (
        <div
          onClick={handleClose}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(45,45,45,0.45)',
            zIndex: 200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}
        >
          {/* Slide-in panel */}
          <div
            onClick={e => e.stopPropagation()}
            style={{
              width: '440px',
              height: '100%',
              background: 'var(--white)',
              boxShadow: 'var(--shadow-lg)',
              display: 'flex',
              flexDirection: 'column',
              animation: 'slideIn 0.22s ease',
            }}
          >
            {/* Modal header */}
            <div style={{
              padding: '1.75rem 2rem 1.25rem',
              borderBottom: '1px solid var(--border)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <div>
                <h2 style={{
                  fontFamily: 'var(--font-heading)',
                  fontSize: '1.6rem',
                  fontWeight: 600,
                  color: 'var(--charcoal)',
                  marginBottom: '0.1rem',
                }}>
                  Add New Client
                </h2>
                <p style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.8rem',
                  color: 'var(--charcoal-muted)',
                }}>
                  Fill in the details below to add a new client account.
                </p>
              </div>
              <button
                onClick={handleClose}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.3rem',
                  color: 'var(--charcoal-muted)',
                  cursor: 'pointer',
                  lineHeight: 1,
                  padding: '0.2rem',
                  marginLeft: '1rem',
                  flexShrink: 0,
                }}
              >
                ✕
              </button>
            </div>

            {/* Form */}
            <form
              onSubmit={handleAddClient}
              style={{ flex: 1, overflowY: 'auto', padding: '1.75rem 2rem' }}
            >
              {[
                { name: 'name',     label: 'Business Name',  placeholder: 'e.g. Blossom Skin Studio',       required: true  },
                { name: 'domain',   label: 'Domain',         placeholder: 'e.g. blossomskin.co',            required: true  },
                { name: 'industry', label: 'Industry',       placeholder: 'e.g. Beauty & Wellness',         required: false },
                { name: 'contact',  label: 'Contact Name',   placeholder: 'e.g. Maya Torres',               required: false },
                { name: 'email',    label: 'Contact Email',  placeholder: 'e.g. maya@blossomskin.co',       required: false },
              ].map(field => (
                <div key={field.name} style={{ marginBottom: '1.25rem' }}>
                  <label style={{
                    fontFamily: 'var(--font-body)',
                    fontSize: '0.72rem',
                    fontWeight: 500,
                    letterSpacing: '0.09em',
                    textTransform: 'uppercase',
                    color: 'var(--charcoal-muted)',
                    display: 'block',
                    marginBottom: '0.35rem',
                  }}>
                    {field.label}{field.required && <span style={{ color: 'var(--blush-dark)', marginLeft: '2px' }}>*</span>}
                  </label>
                  <input
                    type={field.name === 'email' ? 'email' : 'text'}
                    name={field.name}
                    value={form[field.name]}
                    onChange={handleFormChange}
                    placeholder={field.placeholder}
                    style={{
                      fontFamily: 'var(--font-body)',
                      fontSize: '0.875rem',
                      color: 'var(--charcoal)',
                      background: 'var(--white)',
                      border: '1.5px solid var(--border)',
                      borderRadius: 'var(--radius-sm)',
                      padding: '0.6rem 0.85rem',
                      width: '100%',
                      outline: 'none',
                      transition: 'border-color 0.15s',
                    }}
                    onFocus={e => e.target.style.borderColor = 'var(--blush-dark)'}
                    onBlur={e => e.target.style.borderColor = 'var(--border)'}
                  />
                </div>
              ))}

              {formError && (
                <p style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.8rem',
                  color: '#B91C1C',
                  background: '#FDECEA',
                  padding: '0.6rem 0.85rem',
                  borderRadius: 'var(--radius-sm)',
                  marginBottom: '1rem',
                }}>
                  {formError}
                </p>
              )}

              <div style={{
                display: 'flex',
                gap: '0.75rem',
                marginTop: '0.5rem',
              }}>
                <button
                  type="submit"
                  style={{
                    flex: 1,
                    background: 'var(--blush)',
                    color: 'var(--charcoal)',
                    fontFamily: 'var(--font-body)',
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    letterSpacing: '0.03em',
                    padding: '0.7rem 1rem',
                    borderRadius: 'var(--radius-sm)',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'background 0.2s ease',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--blush-dark)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'var(--blush)'}
                >
                  {submitting ? 'Adding…' : 'Add Client'}
                </button>
                <button
                  type="button"
                  onClick={handleClose}
                  style={{
                    flex: 1,
                    background: 'transparent',
                    color: 'var(--charcoal)',
                    fontFamily: 'var(--font-body)',
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    letterSpacing: '0.03em',
                    padding: '0.7rem 1rem',
                    borderRadius: 'var(--radius-sm)',
                    border: '1.5px solid var(--border)',
                    cursor: 'pointer',
                    transition: 'border-color 0.15s, background 0.15s',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.borderColor = 'var(--blush-dark)';
                    e.currentTarget.style.background = 'var(--blush-light)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.borderColor = 'var(--border)';
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to   { transform: translateX(0);    opacity: 1; }
        }
      `}</style>
    </div>
  );
}

function MetaRow({ label, value, italic }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem' }}>
      <span style={{
        fontFamily: 'var(--font-body)',
        fontSize: '0.68rem',
        fontWeight: 500,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: 'var(--charcoal-muted)',
        minWidth: '90px',
        flexShrink: 0,
      }}>
        {label}
      </span>
      <span style={{
        fontFamily: 'var(--font-body)',
        fontSize: '0.82rem',
        color: 'var(--charcoal-light)',
        fontStyle: italic ? 'italic' : 'normal',
      }}>
        {value}
      </span>
    </div>
  );
}
